import React, { useState, useEffect } from 'react';
import {
  Snackbar, Box,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI, systemSettingsAPI, getImageUrl } from '../services/api';
import loginImage from '../assets/login-image.png';
import foodFlowLogo from '../assets/foodflow-logo-w.png';

const isSuperAdminDomain = () =>
  window.location.hostname.startsWith('co.') || window.location.hostname.includes('superadmin');

const Login: React.FC = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [restaurantTitle, setRestaurantTitle] = useState('سامانه مدیریت رستوران');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = isSuperAdminDomain() ? 'سوپر ادمین فودفلو' : 'مدیریت رستوران فودفلو';
    const fetchLogo = async () => {
      try {
        const settings = await systemSettingsAPI.getPublicSettings();
        if (settings.logoUrl) {
          setCustomLogo(getImageUrl(settings.logoUrl));
        }
        if (settings.restaurantName) {
          setRestaurantTitle(settings.restaurantName);
        }
      } catch (err) {
        console.error('Failed to fetch public settings', err);
      }
    };
    fetchLogo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    if (isRegistering) {
      if (password.length < 8) {
        setError('رمز عبور باید حداقل ۸ کاراکتر باشد.');
        setLoading(false);
        return;
      }
      if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
        setError('رمز عبور باید ترکیبی از حروف انگلیسی و اعداد باشد.');
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.register(mobile, password, organizationName);
        setSuccessMsg(response.message || 'ثبت نام با موفقیت انجام شد. اکنون می‌توانید وارد شوید.');
        setIsRegistering(false);
        setOrganizationName('');
        setPassword('');
      } catch (err: any) {
        setError(err.response?.data?.error || 'خطا در ثبت نام سازمان');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      let result;

      if (isSuperAdminDomain()) {
        result = await login(mobile, password, 'superadmin');
      } else {
        result = await login(mobile, password, 'admin');
        if (!result.success && result.error === 'کاربر یافت نشد') {
          result = await login(mobile, password, 'manager');
          if (!result.success && result.error === 'کاربر یافت نشد') {
            result = await login(mobile, password, 'superadmin');
          }
        }
      }

      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'شماره موبایل یا رمز عبور اشتباه است');
      }
    } catch (err) {
      setError('خطا در ورود. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        background: 'linear-gradient(180deg, #306fb6 0%, #2b63a2 100%)',
        overflow: 'hidden',
        direction: 'ltr',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          // Equal halves for perfect centering
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          width: '100%',
          flexGrow: 1,
        }}
      >
        {/* First Child in DOM: Login Form (Appears on the physical RIGHT due to RTL) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            width: '100%',
            height: '100%',
            direction: 'rtl',
            backgroundColor: 'rgba(41, 89, 157, 0.9)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 260,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={foodFlowLogo}
              alt="Logo"
              sx={{
                width: 280,
                //height: auto,
                objectFit: 'contain',
                mb: 0,
              }}
            />

            {window.location.hostname.startsWith('co.') && !isRegistering && (
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontWeight: 600,
                        mt: -2,
                        mb: 4,
                        color: 'rgba(255, 255, 255, 0.7)',
                        textAlign: 'center',
                        letterSpacing: '1px'
                    }}
                >
                    پورتال مدیریت مرکزی
                </Typography>
            )}

            {isRegistering && (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 600,
                  mb: 8,
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                ثبت نام سازمان جدید
              </Typography>
            )}


            <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if (typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
              <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if (typeof setError === 'function') setError(''); }}>
                {error}
              </Alert>
            </Snackbar>

            {successMsg && (
              <Alert severity="success" sx={{ mb: 3, width: '100%', fontFamily: 'Vazir, sans-serif' }}>
                {successMsg}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              {isRegistering && (
                <>
                  <Typography
                    sx={{
                      color: 'white',
                      fontFamily: 'Vazir, sans-serif',
                      fontSize: '0.85rem',
                      mb: 0.5,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    نام سازمان / شرکت / رستوران
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder=""
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    required
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        background: 'white !important',
                        borderRadius: 2,
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: 'Vazir, sans-serif',
                        textAlign: 'right',
                        color: '#757575 !important',
                      },
                    }}
                  />
                </>
              )}

              {/* Label Above */}
              <Typography
                sx={{
                  color: 'white',
                  fontFamily: 'Vazir, sans-serif',
                  fontSize: '0.85rem',
                  mb: 0.5,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                شماره موبایل
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="tel"
                placeholder="09123456789"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                autoComplete="username"
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    background: 'white !important',
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Vazir, sans-serif',
                    textAlign: 'left',
                    color: '#757575 !important',
                    direction: 'ltr',
                    '&::placeholder': {
                      color: '#757575',
                      opacity: 1,
                    },
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px white inset !important',
                      WebkitTextFillColor: '#757575 !important',
                    },
                  },
                }}
              />

              {/* Label Above */}
              <Typography
                sx={{
                  color: 'white',
                  fontFamily: 'Vazir, sans-serif',
                  fontSize: '0.85rem',
                  mb: 0.5,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                رمز عبور
              </Typography>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                placeholder="رمز عبور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={isRegistering ? "new-password" : "current-password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="نمایش رمز عبور"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 5,
                  '& .MuiOutlinedInput-root': {
                    background: 'white !important',
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Vazir, sans-serif',
                    textAlign: 'left',
                    color: '#757575 !important',
                    direction: 'ltr',
                    '&::placeholder': {
                      color: '#757575',
                      opacity: 1,
                    },
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px white inset !important',
                      WebkitTextFillColor: '#757575 !important',
                    },
                  },
                }}
              />

              <Button
                fullWidth
                size="small"
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 600,
                  py: 1.2,
                  mb: 2,
                  backgroundColor: 'rgba(49, 130, 242, 0.9)',
                  borderRadius: 2,
                  color: '#ffffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(62, 137, 240, 0.9)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isRegistering ? 'ثبت نام' : 'ورود'
                )}
              </Button>

              {/* Toggle between login and register - hidden for super admin domain */}
              {!isSuperAdminDomain() && (
                <Button
                  fullWidth
                  size="small"
                  variant="text"
                  onClick={() => {
                    setIsRegistering(!isRegistering);
                    setError('');
                    setSuccessMsg('');
                  }}
                  disabled={loading}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    color: 'white',
                    textDecoration: 'underline',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                      color: '#e0e0e0',
                    },
                  }}
                >
                  {isRegistering ? 'قبلاً ثبت نام کرده‌اید؟ ورود' : 'سازمان جدید هستید؟ ثبت نام رایگان'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Second Child in DOM: Image (Appears on the physical LEFT due to RTL) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
            height: '100%',
            p: 4,
          }}
        >
          <Box
            component="img"
            src={loginImage}
            alt="Side Illustration"
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',

              display: { xs: 'none', sm: 'block' },

            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
